import React, { useMemo, useState } from 'react'

/**
 * FiltersBar: Barra de filtros para el Tablero de Misiones
 * Props:
 *  - filters: {
 *      query: string,
 *      missionTypes: string[],
 *      statuses: string[],
 *      narrativeTimes: string[],
 *      difficulties: string[],
 *      sort: 'recientes' | 'actividad' | 'participantes'
 *    }
 *  - onChangeFilters: (next) => void
 *  - onClear: () => void
 *  - collapsibleChips?: boolean         // Activa colapsado de chips bajo botón "Filtrar"
 *  - defaultCollapsed?: boolean = true  // Estado inicial del panel de chips
 *  - showActiveCount?: boolean = true   // Muestra badge con número de filtros activos
 *  - collapsedLabel?: string = 'Filtrar'
 *  - onToggle?: (collapsed: boolean) => void
 */
export default function FiltersBar({
  filters,
  onChangeFilters,
  onClear,
  collapsibleChips = false,
  defaultCollapsed = true,
  showActiveCount = true,
  collapsedLabel = 'Filtrar',
  onToggle,
}) {
  const missionTypeOptions = ['Misión', 'Combate', 'Espionaje', 'Rescate', 'Exploración', 'Comercio', 'Diplomacia', 'General']
  const statusOptions = [
    { value: 'Abierta', label: 'Abierta' },
    { value: 'En progreso', label: 'En curso' },
    { value: 'Completada', label: 'Cerrada' },
    { value: 'Cancelada', label: 'Cancelada' },
  ]
  const difficultyOptions = ['Social', 'Principiante', 'Intermedio', 'Avanzado', 'Extremo']
  const narrativeTimeOptions = [
    { value: 'presente', label: 'Presente' },
    { value: 'pasado', label: 'Pasado' },
  ]
  const sortOptions = [
    { value: 'recientes', label: 'Recientes' },
    { value: 'actividad', label: 'Actividad' },
    { value: 'participantes', label: 'Participantes' },
  ]

  const toggleChip = (group, value) => {
    const set = new Set(filters[group] || [])
    if (set.has(value)) set.delete(value)
    else set.add(value)
    onChangeFilters({ ...filters, [group]: Array.from(set) })
  }

  const handleQueryChange = (e) => {
    onChangeFilters({ ...filters, query: e.target.value })
  }

  const handleSortChange = (e) => {
    onChangeFilters({ ...filters, sort: e.target.value })
  }

  const isPressed = (group, value) => {
    return Array.isArray(filters[group]) && filters[group].includes(value)
  }

  const activeCount =
    (Array.isArray(filters?.missionTypes) ? filters.missionTypes.length : 0) +
    (Array.isArray(filters?.statuses) ? filters.statuses.length : 0) +
    (Array.isArray(filters?.narrativeTimes) ? filters.narrativeTimes.length : 0) +
    (Array.isArray(filters?.difficulties) ? filters.difficulties.length : 0)

  const renderNarrativeTimeChips = () => (
    <div className="filters-row chips-group" aria-label="Tiempo narrativo">
      <div className="filters-label">Tiempo</div>
      <div className="chips">
        {narrativeTimeOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className="filter-chip"
            aria-pressed={isPressed('narrativeTimes', opt.value)}
            onClick={() => toggleChip('narrativeTimes', opt.value)}
            title={opt.label}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )

  const [collapsed, setCollapsed] = useState(collapsibleChips ? defaultCollapsed : false)
  const panelId = useMemo(() => 'filters-panel-' + Math.random().toString(36).slice(2), [])

  const handleToggle = () => {
    setCollapsed((prev) => {
      const next = !prev
      if (typeof onToggle === 'function') onToggle(next)
      return next
    })
  }

  if (!collapsibleChips) {
    // Layout clásico (sin colapsado): chips visibles + buscador/orden + botón limpiar
    return (
      <div className="filters-bar" role="region" aria-label="Filtros de aventuras">
        <div className="filters-row chips-group" aria-label="Tipo de misión">
          <div className="filters-label">Tipo</div>
          <div className="chips">
            {missionTypeOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className="filter-chip"
                aria-pressed={isPressed('missionTypes', opt)}
                onClick={() => toggleChip('missionTypes', opt)}
                title={opt}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="filters-row chips-group" aria-label="Estado de la misión">
          <div className="filters-label">Estado</div>
          <div className="chips">
            {statusOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="filter-chip"
                aria-pressed={isPressed('statuses', opt.value)}
                onClick={() => toggleChip('statuses', opt.value)}
                title={opt.label}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {renderNarrativeTimeChips()}

        <div className="filters-row chips-group" aria-label="Dificultad">
          <div className="filters-label">Dificultad</div>
          <div className="chips">
            {difficultyOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className="filter-chip"
                aria-pressed={isPressed('difficulties', opt)}
                onClick={() => toggleChip('difficulties', opt)}
                title={opt}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="filters-row inputs-group">
          <div className="filters-search">
            <input
              type="text"
              placeholder="Buscar por título o autor..."
              value={filters.query || ''}
              onChange={handleQueryChange}
              aria-label="Buscar aventuras"
            />
          </div>
          <div className="filters-order">
            <label htmlFor="filters-sort">Orden</label>
            <select id="filters-sort" value={filters.sort || 'recientes'} onChange={handleSortChange} aria-label="Ordenar aventuras">
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filters-actions">
            <button type="button" className="filters-clear-btn" onClick={onClear} title="Limpiar filtros">
              Limpiar
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Layout colapsable: buscador + orden visibles; chips y "Limpiar" dentro del panel
  return (
    <div className="filters-bar" role="region" aria-label="Filtros de aventuras">
      <div className="filters-row inputs-group">
        <div className="filters-search">
          <input
            type="text"
            placeholder="Buscar por título o autor..."
            value={filters.query || ''}
            onChange={handleQueryChange}
            aria-label="Buscar aventuras"
          />
        </div>
        <div className="filters-order">
          <label htmlFor="filters-sort">Orden</label>
          <select id="filters-sort" value={filters.sort || 'recientes'} onChange={handleSortChange} aria-label="Ordenar aventuras">
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filters-toggle">
          <button
            type="button"
            className="filters-toggle-btn"
            aria-expanded={!collapsed}
            aria-controls={panelId}
            onClick={handleToggle}
            title="Mostrar/ocultar filtros"
          >
            {collapsedLabel}
            {showActiveCount && activeCount > 0 && <span className="filters-badge">{activeCount}</span>}
          </button>
        </div>
      </div>

      <div
        id={panelId}
        className={`filters-collapsible ${collapsed ? 'is-collapsed' : 'is-expanded'}`}
        role="region"
        aria-label="Filtros avanzados"
      >
        <div className="filters-row chips-group" aria-label="Tipo de misión">
          <div className="filters-label">Tipo</div>
          <div className="chips">
            {missionTypeOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className="filter-chip"
                aria-pressed={isPressed('missionTypes', opt)}
                onClick={() => toggleChip('missionTypes', opt)}
                title={opt}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="filters-row chips-group" aria-label="Estado de la misión">
          <div className="filters-label">Estado</div>
          <div className="chips">
            {statusOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="filter-chip"
                aria-pressed={isPressed('statuses', opt.value)}
                onClick={() => toggleChip('statuses', opt.value)}
                title={opt.label}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {renderNarrativeTimeChips()}

        <div className="filters-row chips-group" aria-label="Dificultad">
          <div className="filters-label">Dificultad</div>
          <div className="chips">
            {difficultyOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className="filter-chip"
                aria-pressed={isPressed('difficulties', opt)}
                onClick={() => toggleChip('difficulties', opt)}
                title={opt}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="filters-row actions-row">
          <div className="filters-label" />
          <div className="filters-actions">
            <button type="button" className="filters-clear-btn" onClick={onClear} title="Limpiar filtros">
              Limpiar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
